import React, { useEffect, useState, useCallback, useRef } from "react";
import { Row, Col, Container, Button } from "reactstrap";
import MetaTitle from "~/components/Shared/MetaTitle";
import { startAuto, startManual, clearMitekUI, stopSDK } from "~/services/mitek"
import { customMessagesHints } from "~/constants/mitekMessages"
import PreviewCapture from "./Steps/PreviewCapture";
import Preloader from "~/components/Shared/Preloader";
import socketEvent from "~/constants/socketEvent";
import { useSocket } from "~/context/socket";
import IdOptions from "./Steps/IdOptions";
import DriverLicenceInstructions from "./Steps/DriverLicenceInstructions";
import PassportInstructions from "./Steps/PassportInstructions";
import config from "~/config";
import CaptureTypeDialog from "./Partial/CaptureTypeDialog";
import { osName, osVersion, mobileModel, mobileVendor, browserName, browserVersion } from 'react-device-detect';
import { timestamp } from "~/helpers/dateHelper";
import CameraPermissionDenied from "./Partial/CameraPermissionDenied";

type MitekProps = {
  upload: (payload: any) => void;
  id: number;
  consentName: string;
  termsAccepted: boolean;
}

declare const mitekScienceSDK: any;
declare const $: any;

const Mitek = ({ upload, id, consentName, termsAccepted }: MitekProps) => {

  const { socketEmit } = useSocket();

  /********** CONSTANTS **********/

  const DOC_TYPE_DL = "DRIVER_LICENSE";
  const DOC_TYPE_PASSPORT = "PASSPORT";

  const IMAGE_TYPE_DL_FRONT = 'DL_FRONT';
  const IMAGE_TYPE_DL_BACK = 'PDF417_BARCODE';
  const IMAGE_TYPE_PASSPORT = 'PASSPORT';
  const IMAGE_TYPE_SELFIE = 'SELFIE';

  const MODE_AUTO = 'AUTO';
  const MODE_MANUAL = 'MANUAL';

  const STEP_ID_OPTIONS = 3;
  const STEP_INSTRUCTIONS = 4;
  const STEP_CAPTURE = 1;
  const STEP_PREVIEW = 2;
  const STEP_COMPLETE = 5;

  /********** STATE **********/

  const [step, setStep] = useState(STEP_ID_OPTIONS);
  // captured images, in base64 format
  const [docFrontCapture, setDocFrontCapture] = useState('');
  const [docBackCapture, setDocBackCapture] = useState('');
  const [selfieCapture, setSelfieCapture] = useState('');
  // used for preview
  const [currentCapture, setCurrentCapture] = useState('');
  // error received when using manual mode
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  // refers to the type of picture taken (IMAGE_TYPE_DL_FRONT, IMAGE_TYPE_SELFIE, etc)
  const [imageType, setImageType] = useState('');
  // refers to the type of id document used (driver license or passport)
  const [idDocType, setIdDocType] = useState('');
  // capture mode (auto/manual)
  const [mode, setMode] = useState(MODE_AUTO);
  const [showCaptureTypeDialog, setShowCaptureTypeDialog] = useState(false);
  const [showCameraPermissionDialog, setShowCameraPermissionDialog] = useState(false);

  // Track capture modes for each image type
  const [captureModes, setCaptureModes] = useState({
    [IMAGE_TYPE_DL_FRONT]: '',
    [IMAGE_TYPE_DL_BACK]: '',
    [IMAGE_TYPE_PASSPORT]: '',
    [IMAGE_TYPE_SELFIE]: ''
  });

  /********** REFS - Track current state to prevent stale closures **********/

  // Always keep refs in sync with state to avoid closure issues
  const currentModeRef = useRef(mode);
  const currentImageTypeRef = useRef(imageType);
  const currentStepRef = useRef(step);

  // Session management to invalidate old event handlers
  const captureSessionIdRef = useRef(0);
  const isCapturingRef = useRef(false);

  // Cleanup timeout ref to prevent memory leaks
  const sdkCleanupTimeoutRef = useRef<any>(null);

  // Sync refs with state changes
  useEffect(() => {
    currentModeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    currentImageTypeRef.current = imageType;
  }, [imageType]);

  useEffect(() => {
    currentStepRef.current = step;
  }, [step]);

  /********** CLEANUP FUNCTIONS **********/

  // Cleanup all Mitek event listeners
  const cleanupEventListeners = useCallback(() => {
    $(document).off('mitek:media-image-selected');
    $(document).off('mitek:camera-started');
    $(document).off('mitek:image-captured');
    $(document).off('mitek:force-stop');
    $(document).off('mitek:camera-permission-denied');
  }, []);

  // Full capture session cleanup
  const cleanupCapture = useCallback(() => {

    // Clear any pending SDK cleanup timeouts
    if (sdkCleanupTimeoutRef.current) {
      clearTimeout(sdkCleanupTimeoutRef.current);
      sdkCleanupTimeoutRef.current = null;
    }

    // Remove all event listeners
    cleanupEventListeners();

    // Stop SDK and clear UI
    stopSDK();
    clearMitekUI();

    // Reset busy state
    setIsBusy(false);

    isCapturingRef.current = false;

    // Mark as not capturing
    isCapturingRef.current = false;
  }, [cleanupEventListeners]);

  /********** EFFECTS **********/

  // runs once on component mount to emit the `vidProcessStarted` event
  useEffect(() => {
    emitStarted();

    // Cleanup on unmount
    return () => {
      cleanupCapture();
    };
  }, [cleanupCapture]);

  // Handle step changes - single source of truth for capture initiation
  useEffect(() => {

    if (step === STEP_CAPTURE && !isCapturingRef.current) {
      // Start new capture session
      initiateCaptureSession();
    } else if (step === STEP_PREVIEW) {
      // Ensure SDK is stopped when entering preview
      if (isCapturingRef.current) {
        cleanupCapture();
      }
    } else if (step === STEP_COMPLETE) {
      verificationComplete();
    } else if (step !== STEP_CAPTURE && isCapturingRef.current) {
      // Clean up if we leave capture step while still capturing
      cleanupCapture();
    } else {
      console.log('Step is:', step, '- no specific action');
    }
  }, [step]);

  // runs whenever the `imageType` changes
  useEffect(() => {
    if (imageType && currentStepRef.current !== STEP_CAPTURE) {
      // after each image type instructions, step forward
      setStep(STEP_CAPTURE);
    }
  }, [imageType]);

  /********** MITEK CAPTURE SESSION **********/

  // Initialize a new capture session with proper cleanup and session tracking
  const initiateCaptureSession = useCallback(() => {
    if (isCapturingRef.current) {
      return;
    }

    captureSessionIdRef.current += 1;
    const sessionId = captureSessionIdRef.current;
    const captureMode = currentModeRef.current;
    const captureImageType = currentImageTypeRef.current;

    isCapturingRef.current = true;

    cleanupEventListeners();
    stopSDK();

    try {
      if (idDocType === DOC_TYPE_DL) {
        mitekScienceSDK.cmd('COMPONENT_PRELOAD', {
          mitekSDKPath: "./mitekSDK/",
          preloadComponents: [IMAGE_TYPE_DL_FRONT, IMAGE_TYPE_DL_BACK, IMAGE_TYPE_SELFIE],
          options: {
            license: config.MITEK_LICENSE
          }
        });
      } else if (idDocType === DOC_TYPE_PASSPORT) {
        mitekScienceSDK.cmd('COMPONENT_PRELOAD', {
          mitekSDKPath: "./mitekSDK/",
          preloadComponents: [IMAGE_TYPE_PASSPORT, IMAGE_TYPE_SELFIE],
          options: {
            license: config.MITEK_LICENSE
          }
        });
      }
    } catch (error) {
      console.error(`[Session ${sessionId}] Error preloading components:`, error);
    }

    // Setup event listeners... (rest of the code stays the same)
    $(document).one('mitek:media-image-selected', () => {
      if (captureSessionIdRef.current !== sessionId) {
        return;
      }
      setIsBusy(true);
    });

    $(document).one('mitek:camera-started', () => {
      if (captureSessionIdRef.current !== sessionId) {
        return;
      }
      setIsBusy(false);
    });

    $(document).one('mitek:image-captured', (_e: any, captureData: any, error: any) => {
      if (captureSessionIdRef.current !== sessionId) {
        return;
      }

      if (currentModeRef.current !== captureMode) {
        return;
      }

      if (currentStepRef.current !== STEP_CAPTURE) {
        return;
      }

      handleCaptureComplete(captureData, error, sessionId);
    });

    $(document).one('mitek:force-stop', () => {
      if (captureSessionIdRef.current !== sessionId) {
        return;
      }
      setShowCaptureTypeDialog(true);
    });

    $(document).one('mitek:camera-permission-denied', () => {
      if (captureSessionIdRef.current !== sessionId) {
        return;
      }
      setIsBusy(false);
      setShowCameraPermissionDialog(true);
    });

    sdkCleanupTimeoutRef.current = setTimeout(() => {
      if (captureSessionIdRef.current !== sessionId) {
        return;
      }

      if (captureMode === MODE_AUTO) {
        setIsBusy(true);
        startAuto(captureImageType);
      } else {
        $('#captureHeader').removeClass('d-none');
        startManual(captureImageType);
      }

      sdkCleanupTimeoutRef.current = null;
    }, 200); // Increased delay to allow preload to complete
  }, [cleanupEventListeners, idDocType]);

  // Handle completion of image capture
  const handleCaptureComplete = useCallback((captureData: any, error: any, _sessionId: number) => {

    const actualMode = currentModeRef.current;
    const actualImageType = currentImageTypeRef.current;

    setCaptureModes(prev => ({
      ...prev,
      [actualImageType]: actualMode
    }));

    // Full cleanup before navigating to preview
    cleanupCapture();

    // Navigate to preview with captured data
    setCurrentCapture(captureData);
    setError(error || '');
    setStep(STEP_PREVIEW);
  }, [cleanupCapture]);

  /********** TOGGLE FUNCTIONS - FIXED VERSION **********/

  // Switch from AUTO to MANUAL mode
  const switchToManualFromToggle = useCallback(() => {

    if (currentModeRef.current === MODE_MANUAL) {
      return;
    }

    captureSessionIdRef.current += 1;

    // Force stop SDK immediately to prevent late captures
    stopSDK();

    // Wait a bit for SDK to fully stop before cleanup
    setTimeout(() => {
      cleanupCapture();
      setCurrentCapture('');
      setError('');
      setMode(MODE_MANUAL);

      if (currentStepRef.current === STEP_CAPTURE) {

        setTimeout(() => {
          isCapturingRef.current = false;
          initiateCaptureSession();
        }, 200);
      }
    }, 100); // Small delay to ensure SDK stops
  }, [cleanupCapture, initiateCaptureSession]);

  // Switch from MANUAL to AUTO mode
  const switchToAutoFromToggle = useCallback(() => {

    // Ignore if already in AUTO mode
    if (currentModeRef.current === MODE_AUTO) {
      return;
    }

    captureSessionIdRef.current += 1;

    // Full cleanup of MANUAL capture
    cleanupCapture();

    // Clear any lingering capture data
    setCurrentCapture('');
    setError('');

    // Update mode state
    setMode(MODE_AUTO);

    // If we're in STEP_CAPTURE, restart with AUTO mode
    if (currentStepRef.current === STEP_CAPTURE) {
      // Wait for cleanup to complete before starting new session
      setTimeout(() => {
        isCapturingRef.current = false; // Reset capturing flag
        initiateCaptureSession(); // This will use MODE_AUTO from currentModeRef
      }, 200);
    }
  }, [cleanupCapture, initiateCaptureSession]);

  /********** DOCUMENT SELECTION **********/

  const driverLicenseSelected = () => {
    mitekScienceSDK.cmd('COMPONENT_PRELOAD', {
      mitekSDKPath: "./mitekSDK/",
      preloadComponents: [IMAGE_TYPE_DL_FRONT, IMAGE_TYPE_DL_BACK, IMAGE_TYPE_SELFIE],
      options: {
        license: config.MITEK_LICENSE
      }
    });
    setIdDocType(DOC_TYPE_DL);
    setStep(STEP_INSTRUCTIONS);
  }

  const passportSelected = () => {
    mitekScienceSDK.cmd('COMPONENT_PRELOAD', {
      mitekSDKPath: "./mitekSDK/",
      preloadComponents: [IMAGE_TYPE_PASSPORT, IMAGE_TYPE_SELFIE],
      options: {
        license: config.MITEK_LICENSE
      }
    });
    setIdDocType(DOC_TYPE_PASSPORT);
    setStep(STEP_INSTRUCTIONS);
  }

  /********** CAPTURE CONTROL **********/

  const captureDocument = (docType: string) => {

    // Reset to AUTO mode for each new document scan
    setMode(MODE_AUTO);

    // Clear any previous capture data
    setCurrentCapture('');
    setError('');

    // Set the image type (this will trigger useEffect to move to STEP_CAPTURE)
    setImageType(docType);
    setTimeout(() => {
      setImageType(docType);
    }, 0);
  }

  const acceptCapture = () => {

    // Save the captured image to the appropriate state
    switch (imageType) {
      case IMAGE_TYPE_DL_FRONT:
      case IMAGE_TYPE_PASSPORT:
        setDocFrontCapture(currentCapture);
        break;
      case IMAGE_TYPE_DL_BACK:
        setDocBackCapture(currentCapture);
        break;
      case IMAGE_TYPE_SELFIE:
        setSelfieCapture(currentCapture);
        break;
    }

    // Clear imageType to allow retake to work immediately
    setImageType('');

    // Clear current capture
    setCurrentCapture('');

    // Reset mode to AUTO for next capture
    setMode(MODE_AUTO);

    // After accepting, go back to instructions
    setStep(STEP_INSTRUCTIONS);
  }

  const retry = () => {
    // Clear previous capture
    setCurrentCapture('');
    setError('');

    // Go back to capture step (will initiate new session)
    setStep(STEP_CAPTURE);
  }

  /********** DIALOG HANDLERS - FIXED **********/

  const selectManualMode = () => {

    // Close dialog first
    setShowCaptureTypeDialog(false);

    // Invalidate current session
    captureSessionIdRef.current += 1;

    // Full cleanup
    cleanupCapture();

    // Clear any previous data
    setCurrentCapture('');
    setError('');

    // Set to MANUAL mode
    setMode(MODE_MANUAL);

    // Ensure we restart capture session
    // We need to allow time for dialog to close and state to update
    setTimeout(() => {

      isCapturingRef.current = false;

      // Force step to STEP_CAPTURE to trigger useEffect
      if (currentStepRef.current === STEP_CAPTURE) {
        // If already in STEP_CAPTURE, manually trigger initiate
        initiateCaptureSession();
      } else {
        // Otherwise set step which will trigger useEffect
        setStep(STEP_CAPTURE);
      }
    }, 300); // Longer delay to ensure dialog is closed
  }

  const selectAutoMode = () => {
    // Close dialog first
    setShowCaptureTypeDialog(false);

    // Invalidate current session
    captureSessionIdRef.current += 1;
    // Full cleanup
    cleanupCapture();
    // Clear any previous data
    setCurrentCapture('');
    setError('');
    // Ensure we're in AUTO mode
    setMode(MODE_AUTO);
    // CRITICAL: Ensure we restart capture session
    setTimeout(() => {
      isCapturingRef.current = false;

      // Force step to STEP_CAPTURE to trigger useEffect
      if (currentStepRef.current === STEP_CAPTURE) {
        // If already in STEP_CAPTURE, manually trigger initiate
        initiateCaptureSession();
      } else {
        // Otherwise set step which will trigger useEffect
        setStep(STEP_CAPTURE);
      }
    }, 200); // Longer delay to ensure dialog is closed
  }

  const exitCaptureTypeDialog = () => {
    // Close dialog
    setShowCaptureTypeDialog(false);

    // Reset imageType to allow same button to work again
    setImageType('');

    // Clear any capture data
    setCurrentCapture('');
    setError('');

    // Reset mode to AUTO
    setMode(MODE_AUTO);

    // Go back to instructions page
    setStep(STEP_INSTRUCTIONS);
  }

  const exitCameraPermissionDialog = () => {
    setShowCameraPermissionDialog(false);
    // go back to instructions page
    setStep(STEP_INSTRUCTIONS);
    // reset mode to AUTO
    setMode(MODE_AUTO);
  }

  /********** NAVIGATION **********/

  const clearMitek = () => {

    // CRITICAL: Reset mode to AUTO FIRST, before cleanup
    setMode(MODE_AUTO);

    // Then do cleanup
    cleanupCapture();

    // Reset imageType to allow same button to work
    setImageType('');

    // Reset to instructions
    setStep(STEP_INSTRUCTIONS);

  }

  const back = () => {
    // Clean up SDK when changing document type
    cleanupCapture();

    // Reset all state when going back to document selection
    setStep(STEP_ID_OPTIONS);
    setIdDocType('');
    setImageType('');
    setDocFrontCapture('');
    setDocBackCapture('');
    setSelfieCapture('');
    setCurrentCapture('');
    setError('');
    setMode(MODE_AUTO);
    setCaptureModes({
      [IMAGE_TYPE_DL_FRONT]: '',
      [IMAGE_TYPE_DL_BACK]: '',
      [IMAGE_TYPE_PASSPORT]: '',
      [IMAGE_TYPE_SELFIE]: ''
    });
  }

  /********** BACKEND CALLS **********/

  const verificationComplete = () => {

    // Build capture mode info based on document type
    let captureModeInfo = {};

    if (idDocType === DOC_TYPE_DL) {
      // Driver License: 3 captures
      captureModeInfo = {
        frontCaptureMode: captureModes[IMAGE_TYPE_DL_FRONT],
        backCaptureMode: captureModes[IMAGE_TYPE_DL_BACK],
        selfieCaptureMode: captureModes[IMAGE_TYPE_SELFIE]
      };
    } else if (idDocType === DOC_TYPE_PASSPORT) {
      // Passport: 2 captures
      captureModeInfo = {
        frontCaptureMode: captureModes[IMAGE_TYPE_PASSPORT],
        selfieCaptureMode: captureModes[IMAGE_TYPE_SELFIE]
      };
    }

    const payload = {
      termsAccepted: termsAccepted,
      consentName: consentName,
      IDType: idDocType,
      docFrontCapture: docFrontCapture.replace('data:image/jpeg;base64,', ''),
      docBackCapture: docBackCapture.replace('data:image/jpeg;base64,', ''),
      selfieCapture: selfieCapture.replace('data:image/jpeg;base64,', ''),
      ...captureModeInfo,
      userAgent: {
        captureTime: timestamp(),
        osName: osName,
        osVersion: osVersion,
        device: `${mobileVendor} ${mobileModel}`,
        browserName: browserName,
        browserVersion: browserVersion,
      }
    }

    upload(payload);
  }

  /********** SOCKET EVENTS **********/

  // Notify desktop that id verification started
  const emitStarted = () => {
    socketEmit(socketEvent.vidProcessStarted, { id: id })
  }

  /********** RENDER **********/

  return (
    <React.Fragment>
      <MetaTitle>ID Validation</MetaTitle>
      <div className="account-pages">
        <Container>
          {showCaptureTypeDialog && (
            <CaptureTypeDialog
              retryAutocapture={selectAutoMode}
              switchToManual={selectManualMode}
              exitDialog={exitCaptureTypeDialog}
            />
          )}
          {showCameraPermissionDialog && (
            <CameraPermissionDenied exitDialog={exitCameraPermissionDialog} />
          )}

          <div id="captureHeader" className="d-none picture-header">
            <div id="headerText">{customMessagesHints[imageType]}</div>

            {/* Toggle Button for Auto/Manual Capture */}
            <div className="toggle-container">
              <div className="toggle-button">
                <span
                  className={mode === MODE_MANUAL ? 'active' : ''}
                  onClick={switchToManualFromToggle}
                >
                  Manual
                </span>
                <span
                  className={mode === MODE_AUTO ? 'active' : ''}
                  onClick={switchToAutoFromToggle}
                >
                  Autocapture
                </span>
              </div>
            </div>

            <Button id="closeCaptureBtn" onClick={clearMitek}>
              <i className="fas fa-times"></i>
            </Button>
          </div>

          <Row className="justify-content-center">
            <Col md={9} lg={6} xl={5} className="full-height-column p-0">
              <div className="overflow-hidden" id="vid_mobile_main_card">
                {step === STEP_ID_OPTIONS && (
                  <IdOptions
                    driverLicenseSelected={driverLicenseSelected}
                    passportSelected={passportSelected}
                  />
                )}
                {step === STEP_INSTRUCTIONS && idDocType === DOC_TYPE_DL && (
                  <DriverLicenceInstructions
                    frontSrc={docFrontCapture}
                    backSrc={docBackCapture}
                    selfieSrc={selfieCapture}
                    startCapture={captureDocument}
                    back={back}
                    complete={verificationComplete}
                  />
                )}
                {step === STEP_INSTRUCTIONS && idDocType === DOC_TYPE_PASSPORT && (
                  <PassportInstructions
                    frontSrc={docFrontCapture}
                    selfieSrc={selfieCapture}
                    startCapture={captureDocument}
                    back={back}
                    complete={verificationComplete}
                  />
                )}
                {step === STEP_PREVIEW && (
                  <PreviewCapture
                    previewImage={currentCapture}
                    error={error}
                    retry={retry}
                    cancel={clearMitek}
                    accept={acceptCapture}
                    imageType={imageType}
                  />
                )}
              </div>
            </Col>
          </Row>
          {isBusy && <Preloader />}
        </Container>
      </div>
    </React.Fragment>
  );
}

export default Mitek;